interface ConditionObject {
  [key: string]: string
}
type Condition = ConditionObject | ConditionObject[] | string[] | string

type StorageType = 'local' | 'session'

export class LocalStorage {
  private key: string
  private type: StorageType
  // Cached per storage type — local and session availability can differ.
  private static isAvailableFlag: Partial<Record<StorageType, boolean>> = {}

  constructor(key: string, type?: StorageType) {
    this.key = key
    this.type = type ?? 'local'
  }

  static isAvailable(type: StorageType): boolean {
    if (LocalStorage.isAvailableFlag[type]) return true

    LocalStorage.isAvailableFlag[type] = LocalStorage.checkAvailability(type)

    return LocalStorage.isAvailableFlag[type] ?? false
  }

  static getStorage(type: StorageType) {
    if (typeof window === 'undefined') return null

    return type === 'session' ? window.sessionStorage : window.localStorage
  }

  static clear(type?: StorageType) {
    const validType = type ?? 'local'

    if (LocalStorage.checkAvailability(validType)) {
      const storage = LocalStorage.getStorage(validType)

      if (storage) storage.clear()
    }
  }

  static keys(type?: StorageType) {
    const validType = type ?? 'local'

    if (!LocalStorage.isAvailable(validType)) return []
    const storage = LocalStorage.getStorage(validType)

    if (!storage) return []

    return Object.keys(storage)
  }

  private static checkAvailability(type: StorageType): boolean {
    try {
      if (typeof window === 'undefined') return false

      const testKey = '__storage_test__'
      const storage = LocalStorage.getStorage(type)

      if (!storage) return false

      storage.setItem(testKey, testKey)
      storage.removeItem(testKey)

      return true
    } catch {
      return false
    }
  }

  get = <T>(defaultValue: T, condition?: Condition): T => {
    try {
      if (!LocalStorage.isAvailable(this.type)) return defaultValue

      const storage = LocalStorage.getStorage(this.type)

      if (!storage) return defaultValue

      const item = storage.getItem(this.key)

      if (!item) return defaultValue

      const parsedItem = JSON.parse(item) as T

      if (condition) {
        return this.validate(parsedItem, condition) ? parsedItem : defaultValue
      }

      return parsedItem
    } catch (error) {
      console.error(`Error retrieving ${this.key} from localStorage:`, error)

      return defaultValue
    }
  }

  set = (value: unknown): boolean => {
    try {
      if (!LocalStorage.isAvailable(this.type)) return false

      const storage = LocalStorage.getStorage(this.type)

      if (!storage) return false

      storage.setItem(this.key, JSON.stringify(value))

      return true
    } catch (error) {
      console.error(`Error saving ${this.key} to localStorage:`, error)

      return false
    }
  }

  remove = (): boolean => {
    try {
      if (!LocalStorage.isAvailable(this.type)) return false

      const storage = LocalStorage.getStorage(this.type)

      if (!storage) return false

      storage.removeItem(this.key)

      return true
    } catch (error) {
      console.error(`Error removing ${this.key} from localStorage:`, error)

      return false
    }
  }
  //expecting sets:
  // - basic type value - condition is string type
  // - object value - condition is object
  // - array basic value - condition is array of string type
  // - array object value - condition is array of objects types
  private validate = (value: unknown, condition: Condition): boolean => {
    if (typeof condition === 'string') {
      return typeof value === condition
    }

    if (
      typeof condition === 'object' &&
      !Array.isArray(condition) &&
      condition !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value !== null
    ) {
      // Deep validation for nested objects
      return Object.entries(condition).every(([key, expectedType]) => {
        const val = (value as Record<string, unknown>)[key]

        if (typeof expectedType === 'string') {
          return typeof val === expectedType
        } else if (typeof expectedType === 'object') {
          return this.validate(val, expectedType)
        }

        return false
      })
    }

    if (Array.isArray(condition) && Array.isArray(value)) {
      if (typeof condition?.[0] === 'string') {
        return value.every(
          (v, i) => typeof v === condition[i] || typeof v === condition[0]
        )
      }

      // Deep validation for array of objects/arrays
      return value.every((v, i) =>
        this.validate(v, condition[i] || condition[0])
      )
    }

    return false
  }
}
